import { createModel } from '@rematch/core';
import fromPairs from 'lodash/fromPairs';
import moment from 'moment';
import { assessmentsService } from 'src/config/rootService';
import { RootModel } from 'src/config/rootStore';

import {
    Assessment,
    AssessmentsState,
    AssessmentType,
    MappedAnswers,
    MappedScores,
} from '../models/assessments';

const defaultState: AssessmentsState = {
    assessments: {},
    scores: {
        [AssessmentType.Who5]: undefined,
        [AssessmentType.EmploymentHealth]: undefined,
        [AssessmentType.Phq2]: undefined,
        [AssessmentType.Phq8]: undefined,
        [AssessmentType.Gad2]: undefined,
        [AssessmentType.Gad7]: undefined,
    },
    mostRecentAssessmentDate: '',
};

export const assessments = createModel<RootModel>()({
    state: defaultState,
    reducers: {
        saveScores: (state, nextScores: MappedScores): AssessmentsState => {
            const newSate = {
                ...state,
                scores: {
                    ...state.scores,
                    ...nextScores,
                },
            };

            return newSate;
        },
        saveAssessmentData: (state, fetchedAssessments: Assessment[]): AssessmentsState => {
            const newState: AssessmentsState = {
                ...state,
                assessments: fromPairs(
                    fetchedAssessments.map(assessment => [assessment.name, assessment]),
                ),
            };

            return newState;
        },
        saveMostRecentAssessmentDate: (state, date: string): AssessmentsState => {
            const newState: AssessmentsState = {
                ...state,
                mostRecentAssessmentDate: date,
            };

            return newState;
        },
        'authentication/logout': (): AssessmentsState => {
            return defaultState;
        },
    },
    effects: dispatch => ({
        async fetchAssessments(): Promise<void> {
            const fetchAssessments = await assessmentsService.fetchAssessments();

            dispatch.assessments.saveAssessmentData(fetchAssessments);
        },
        async submitAssessments({
            answers,
            assessment,
            careRecommendationUuid,
        }: {
            answers: MappedAnswers;
            assessment: {
                name: string;
                assessmentFlowUuid: string;
            };
            careRecommendationUuid?: string;
        }): Promise<{ scores: Record<string, number>; nextAssessment: string }> {
            const { scores, nextAssessment } =
                await assessmentsService.submitModularizedAssessments(
                    answers,
                    assessment,
                    careRecommendationUuid,
                );

            dispatch.assessments.saveScores(scores);
            dispatch.assessments.saveMostRecentAssessmentDate(moment().toISOString());

            return {
                scores,
                nextAssessment,
            };
        },
        async fetchLatestScores(): Promise<Record<string, number>> {
            const { scores, date } = await assessmentsService.fetchScores();

            dispatch.assessments.saveScores(scores);
            dispatch.assessments.saveMostRecentAssessmentDate(date);

            return scores;
        },
    }),
});
